import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";

const COMMISSION_TYPE = {
  percent_net: "% of Net Sale",
  percent_gross: "% of Gross Sale",
  flat_per_sale: "Flat $ Per Sale",
  custom: "Custom",
};
const TERM = {
  month_to_month: "Month-to-month",
  "3m": "3 months",
  "6m": "6 months",
  "12m": "12 months",
  custom: "Custom",
};

export default function PartnerOffer() {
  const { id } = useParams();
  const nav = useNavigate();
  const [offer, setOffer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    http.get(`/partner/offers/${id}`)
      .then(({ data }) => setOffer(data))
      .catch((err) => {
        // 401 → response interceptor already redirects to /login?next=…
        if (err.response?.status !== 401) {
          toast.error(err.response?.data?.detail || "Offer not found.");
          nav("/partner");
        }
      });
  }, [id, nav]);

  const accept = async () => {
    if (!window.confirm("Accept this offer? You'll be taken to the agreement to sign.")) return;
    setBusy(true);
    try {
      const { data } = await http.post(`/partner/offers/${id}/accept`);
      toast.success("Offer accepted. Sign your agreement next.");
      nav(`/partner/agreement/${data.agreement_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't accept.");
    } finally { setBusy(false); }
  };

  const decline = async () => {
    if (!window.confirm("Decline this offer? You can still message Mickey if you want different terms.")) return;
    setBusy(true);
    try {
      await http.post(`/partner/offers/${id}/decline`, { reason });
      toast.success("Offer declined.");
      nav("/partner");
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't decline."); }
    finally { setBusy(false); }
  };

  if (!offer) return <Layout><div className="py-32 text-center text-[#8a857d]"><Loader2 className="animate-spin inline-block" size={16} /></div></Layout>;

  const rate = offer.commission_rate <= 1 ? `${(offer.commission_rate * 100).toFixed(1)}%` : `${offer.commission_rate}`;

  return (
    <Layout>
      <section className="py-16 px-6">
        <div className="max-w-2xl mx-auto">
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">Your Host Offer</div>
          <h1 className="font-display text-4xl text-[#F4F1EA] mb-2">Review your terms</h1>
          <p className="font-body text-[#B5B0A7] mb-8">
            Below is the custom offer from Simple Truths Club. Accept to receive
            your agreement to sign, or decline if you&apos;d like different terms.
          </p>

          <div className="border border-[#C9A055]/30 bg-[#0a0a0a] p-6 space-y-4" data-testid="offer-summary">
            <Row k="Commission Rate" v={rate} highlight />
            <Row k="Commission Type" v={COMMISSION_TYPE[offer.commission_type] || offer.commission_type} />
            <Row k="Approved Pieces" v={offer.approved_piece_count} />
            <Row k="Agreement Term" v={TERM[offer.term_length] || offer.term_length} />
            <Row k="Payout Schedule" v={offer.payout_schedule} />
            {offer.payout_threshold > 0 && <Row k="Min Payout Threshold" v={`$${offer.payout_threshold.toFixed(2)}`} />}
            {offer.display_requirements && <Row k="Display Requirements" v={offer.display_requirements} />}
            {offer.shipping_notes && <Row k="Shipping / Delivery" v={offer.shipping_notes} />}
            {offer.custom_terms && (
              <div className="pt-2 border-t border-white/10">
                <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">Custom Terms</div>
                <p className="text-[13px] text-[#E8E4DC] whitespace-pre-wrap">{offer.custom_terms}</p>
              </div>
            )}
            {offer.expires_at && (
              <div className="text-[12px] text-amber-300">⏰ Expires {new Date(offer.expires_at).toLocaleDateString()}</div>
            )}
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            <button onClick={accept} disabled={busy} data-testid="offer-accept"
              className="btn-gold inline-flex items-center justify-center gap-2 py-4 disabled:opacity-50">
              Accept &amp; Sign <ArrowRight size={14} />
            </button>
            <button onClick={() => setReason(reason || "Custom terms needed")} className="btn-outline py-4">
              Request Different Terms
            </button>
          </div>

          {reason !== "" && (
            <div className="mt-4 border border-white/10 bg-[#0a0a0a] p-4 space-y-2">
              <label className="block">
                <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">What would you change?</div>
                <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                  data-testid="offer-decline-reason"
                  className="stc-input w-full font-body" />
              </label>
              <div className="flex justify-end gap-2">
                <button onClick={() => setReason("")} className="btn-outline">Nevermind</button>
                <button onClick={decline} data-testid="offer-decline"
                  className="btn-outline border-red-700/40 text-red-300">Decline Offer</button>
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}

function Row({ k, v, highlight }) {
  return (
    <div className="flex justify-between items-baseline gap-4 border-b border-white/5 pb-2">
      <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d]">{k}</div>
      <div className={`font-${highlight ? "display text-3xl" : "body text-[14px]"} ${highlight ? "text-[#C9A055]" : "text-[#F4F1EA]"}`}>{v}</div>
    </div>
  );
}
